import { Formik } from 'formik'
import { keys, isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import { SelectTags } from 'components/v2/SelectTags'
import { Tag } from 'components/v2/Tag'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { DOW } from 'features/FareFamilies/components/Dow/constants'
import { dowMessages } from 'features/FareFamilies/components/Dow/messages'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { getQueryString } from 'utils/route'
import {
  FilterHeader,
  FilterToggle,
  FilterWrapper,
  FiltersContainer,
  FiltersSubmit,
  FiltersWrapper,
  TagsWrapper
} from 'components/Filters'
import { getRouteServiceLabel, RouteServiceAutocomplete } from 'features/Services/components/RouteServiceAutocomplete'
import { GTRouteServiceSuggestion } from 'gql/types.generated'

const DOW_LIST = [DOW.MONDAY, DOW.TUESDAY, DOW.WEDNESDAY, DOW.THURSDAY, DOW.FRIDAY, DOW.SATURDAY, DOW.SUNDAY]

const messages = defineMessages({
  selectOriginCity: {
    id: 'fareRules.filters.selectOriginCity',
    defaultMessage: 'Select origin city'
  },
  selectDestinationCity: {
    id: 'fareRules.filters.selectDestinationCity',
    defaultMessage: 'Select destination city'
  },
  selectOrigin: {
    id: 'fareRules.filters.selectOrigin',
    defaultMessage: 'Select origin'
  },
  selectDestination: {
    id: 'fareRules.filters.selectDestination',
    defaultMessage: 'Select destination'
  },
  selectService: {
    id: 'fareRules.filters.selectService',
    defaultMessage: 'Select service'
  },
  selectDow: {
    id: 'fareRules.filters.selectDow',
    defaultMessage: 'Select day of week'
  }
})

const defaultValues = {
  originCity: { id: '', name: '' },
  destinationCity: { id: '', name: '' },
  origin: { id: '', name: '' },
  destination: { id: '', name: '' },
  routeService: { id: '' },
  dow: []
}

const getFilteredDow = (values: TSFixMe): TSFixMe => {
  return DOW_LIST.filter((day): TSFixMe => {
    return values.indexOf(day) === -1
  })
}

export const FareRulesFilters = ({ filters }: TSFixMe): TSFixMe => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): TSFixMe => {
    showFilters(!isShowingFilters)
  }

  const filterFareRules = (values: TSFixMe): TSFixMe => {
    const query = {
      originCityId: values.originCity.id,
      destinationCityId: values.destinationCity.id,
      originId: values.origin.id,
      destinationId: values.destination.id,
      routeServiceId: values.routeService.id,
      dow: values.dow.join(',')
    }

    navigate(`/fare-rules?${getQueryString(query)}`)
  }

  const initialValues = {
    originCity: filters.originCity || defaultValues.originCity,
    destinationCity: filters.destinationCity || defaultValues.destinationCity,
    origin: filters.origin || defaultValues.origin,
    destination: filters.destination || defaultValues.destination,
    routeService: filters.routeService || defaultValues.routeService,
    dow: filters.dow || defaultValues.dow
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterFareRules} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, setFieldValue, values } = props

        const handleFilterDelete = (id: TSFixMe): TSFixMe => {
          /*@ts-expect-error ts(2322) FIXME*/
          setFieldValue(id, defaultValues[id])
          filterFareRules({
            ...values,
            /*@ts-expect-error ts(2322) FIXME*/
            [id]: defaultValues[id]
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = filters[filterKey]

                      const hasValue = (filterValue && filterValue.getTime) || !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'originCity':
                          label = <FormattedMessage id="dictionary.originCity" defaultMessage="Origin city" />
                          value = filterValue.name
                          break
                        case 'destinationCity':
                          label = <FormattedMessage id="dictionary.destinationCity" defaultMessage="Destination city" />
                          value = filterValue.name
                          break
                        case 'origin':
                          label = <FormattedMessage id="dictionary.origin" defaultMessage="Origin" />
                          value = filterValue.name
                          break
                        case 'destination':
                          label = <FormattedMessage id="dictionary.destination" defaultMessage="Destination" />
                          value = filterValue.name
                          break
                        case 'routeService':
                          label = <FormattedMessage id="dictionary.service" defaultMessage="Service" />
                          value = getRouteServiceLabel(filterValue)
                          break
                        case 'dow':
                          label = <FormattedMessage id="dictionary.dow" defaultMessage="DOW" />
                          value = filterValue
                            .map((day: TSFixMe): TSFixMe => {
                              return dowMessages[day] && intl.formatMessage(dowMessages[day])
                            })
                            .join(', ')
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag key={filterKey} onDelete={handleFilterDelete.bind(this, filterKey)}>
                          {label}: {value}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <CityAutocomplete
                    value={values.originCity.name}
                    onChange={(city): TSFixMe => {
                      setFieldValue('originCity', city)

                      filterFareRules({
                        ...values,
                        originCity: city
                      })
                    }}
                    placeholder={intl.formatMessage(messages.selectOriginCity)}
                    label={<FormattedMessage id="dictionary.originCity" defaultMessage="Origin city" />}
                  />
                  <CityAutocomplete
                    value={values.destinationCity.name}
                    onChange={(city): TSFixMe => {
                      setFieldValue('destinationCity', city)

                      filterFareRules({
                        ...values,
                        destinationCity: city
                      })
                    }}
                    placeholder={intl.formatMessage(messages.selectDestinationCity)}
                    label={<FormattedMessage id="dictionary.destinationCity" defaultMessage="Destination city" />}
                  />
                  <PlaceAutocomplete
                    placeholder={intl.formatMessage(messages.selectOrigin)}
                    value={values.origin.name}
                    label={<FormattedMessage id="dictionary.origin" defaultMessage="Origin" />}
                    onChange={(place: TSFixMe): TSFixMe => {
                      setFieldValue('origin', place)

                      filterFareRules({
                        ...values,
                        origin: place
                      })
                    }}
                  />
                  <PlaceAutocomplete
                    placeholder={intl.formatMessage(messages.selectDestination)}
                    value={values.destination.name}
                    label={<FormattedMessage id="dictionary.destination" defaultMessage="Destination" />}
                    onChange={(place: TSFixMe): TSFixMe => {
                      setFieldValue('destination', place)

                      filterFareRules({
                        ...values,
                        destination: place
                      })
                    }}
                  />
                  <RouteServiceAutocomplete
                    placeholder={intl.formatMessage(messages.selectService)}
                    value={getRouteServiceLabel(values.routeService) || ''}
                    label={<FormattedMessage id="dictionary.service" defaultMessage="Service" />}
                    onChange={(routeService: GTRouteServiceSuggestion): void => {
                      setFieldValue('routeService', routeService)

                      filterFareRules({
                        ...values,
                        routeService
                      })
                    }}
                  />
                  <SelectTags
                    placeholder={intl.formatMessage(messages.selectDow)}
                    values={values.dow}
                    items={getFilteredDow(values.dow)}
                    getTagLabel={(day: TSFixMe): TSFixMe => {
                      return intl.formatMessage(dowMessages[day])
                    }}
                    onChange={(dow: TSFixMe): TSFixMe => {
                      setFieldValue('dow', dow)

                      filterFareRules({
                        ...values,
                        dow
                      })
                    }}
                    label={<FormattedMessage id="dictionary.dow" defaultMessage="Dow" />}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
